/* eslint-disable jsx-a11y/click-events-have-key-events */

import { useState, useContext } from 'react'
import Cookie from 'js-cookie'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { useRouter } from 'next/router'

import useScrollPosition from 'lib/hooks/useScrollPosition'
import useSession from 'lib/network/hooks/useSession'
import useAppConfig from 'lib/network/hooks/useAppConfig'
import useIsMobile from 'lib/hooks/useIsMobile'
import { useIsLargeTablet } from 'lib/hooks/useIsTablet'

import importEverlaneIcon, { Icon } from 'lib/iconProps'
import { getNavigationBaseUrl } from 'lib/helpers/navigationV2'
import { enablePageScroll, disablePageScroll } from 'lib/helpers/scroll_helpers'

import NavigationContext from 'contexts/NavigationContext'
import AuthModal from 'components/auth/AuthModal'
import { ModalStates } from 'components/auth/AuthModal/CopyTypes'
import { Media } from 'components/core/BreakpointMedia'

import Cookies from 'types/Cookies'
import { trackBuilderClicks } from './menuHelper'

import EverlaneLogo from './EverlaneLogo'

import styles from './styles.module.scss'

const X = importEverlaneIcon(Icon.X)
const MenuIcon = importEverlaneIcon(Icon.MENU)

const AccountDropdown = dynamic(() => import('./DesktopMenu/AccountDropdown'), { ssr: false })
const MobileMenu = dynamic(() => import('./MobileMenu'), { ssr: false })
const RegionDropdown = dynamic(() => import('./RegionDropdown'), { ssr: false })

export const NAV_BAR_HEIGHT = 40
export const navbarId = 'navbar'

declare global {
  interface Window {
    HAS_LOGGED_ENGAGEMENT: boolean
    LUX: any
  }
}

const Navbar = ({ showMenuIcon, showBagIcon }) => {
  const isMobile = useIsMobile()
  const isTablet = useIsLargeTablet()

  const isDesktop = !isMobile && !isTablet

  const router = useRouter()
  const isCheckoutPath = router.asPath.includes('checkout')

  const { data: appConfig } = useAppConfig()
  const { data: session } = useSession()

  const { showMinimalNav = false } = appConfig || {}
  // Never show minimal nav on admin.everlane.com - admin users need full navigation access
  const isAdminDomain =
    typeof window !== 'undefined' && window.location.hostname === 'admin.everlane.com'
  const shouldShowMinimalNav = showMinimalNav && !isAdminDomain

  const {
    isNavigationMenuOpen,
    setIsNavigationMenuOpen,
    menus,
    isRegionDropdownOpen,
    setIsRegionDropdownOpen,
    isBannerSticky,
    closeSubMenu,
  } = useContext(NavigationContext)

  const [isNewUser, setIsNewUser] = useState(null)
  const [isSubNavHidden, setIsSubNavHidden] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isAccountDropdownOpened, setIsAccountDropdownOpened] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [prevClientScrollSt, setPrevClientScrollSt] = useState(0)

  const mobileMenus = menus?.filter(menu => menu.platform !== 'desktop')
  const desktopMenus = menus?.filter(menu => menu.platform !== 'mobile')

  const { visitor } = session ?? {}
  const { email: visitorEmail = null } = visitor ?? {}

  // Sticky navbar
  useScrollPosition(
    ({ currPos, prevPos }) => {
      const clientScrollY = currPos.y * -1
      const prevClientScrollY = prevPos.y * -1
      setPrevClientScrollSt(prevClientScrollY)
      if (isCheckoutPath || shouldShowMinimalNav) setIsSubNavHidden(true)
      else if (!isDesktop) setIsSubNavHidden(true)
      else if (isHovering) setIsSubNavHidden(false)
      else {
        setPrevClientScrollSt(prevClientScrollY)
        if (clientScrollY === prevClientScrollSt) return
        if (clientScrollY < prevClientScrollY) setIsSubNavHidden(false)
        else setIsSubNavHidden(clientScrollY > 40 && !isHovering)
      }
    },
    // the prevClientScrollSt a dependency that will reduce flashing as it resets the values used inside the hook
    [prevClientScrollSt],
    null,
    null,
    100,
  )
  let modalState: string
  if (isNewUser === true) {
    modalState = ModalStates.SIGN_UP
  } else if (isNewUser === false) {
    modalState = ModalStates.LOG_IN
  }

  return (
    <nav
      id={navbarId}
      className={classNames(styles['navbar-container'], {
        [styles['navbar-container--subnav-hidden']]: isSubNavHidden,
        [styles['navbar-container--minimal']]: !isMobile && !showMenuIcon && !showBagIcon,
        [styles['navbar-container--sticky-banner']]: isBannerSticky,
      })}
      onMouseLeave={() => {
        setIsAccountDropdownOpened(false)
        setIsHovering(false)
      }}
      onMouseEnter={() => {
        if (!isCheckoutPath && !shouldShowMinimalNav) {
          setIsSubNavHidden(false)
          setIsHovering(true)
        }
      }}
    >
      <div
        className={classNames(styles.navbar__wrapper, {
          [styles['navbar__wrapper--minimal']]: !showMenuIcon && !showBagIcon,
        })}
      >
        <div className={styles['navbar__wrapper-container']}>
          <div className={styles['navbar__menu-left']}>
            {showMenuIcon && mobileMenus && (
              <Media lessThan="largeTablet">
                <div className={styles['navbar__icon-wrapper']}>
                  <button
                    type="button"
                    className={styles['navbar__icon-button']}
                    aria-expanded={isNavigationMenuOpen}
                    onClick={() => {
                      if (!isNavigationMenuOpen) {
                        setIsNavigationMenuOpen(true)
                        disablePageScroll()
                      } else {
                        setIsNavigationMenuOpen(false)
                        enablePageScroll()
                      }
                    }}
                  >
                    {isNavigationMenuOpen ? (
                      <X height="20px" width="20px" />
                    ) : (
                      <MenuIcon height="20px" width="20px" />
                    )}
                  </button>
                </div>
              </Media>
            )}
            {showMenuIcon && (
              <Media greaterThanOrEqual="largeTablet">
                <ul className={styles.navbar__categories}>
                  {desktopMenus?.map(category => (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <li
                      key={category.text}
                      className={classNames(styles.navbar__category)}
                      onClick={() => {
                        trackBuilderClicks(category)
                        router.push(category.url)
                      }}
                    >
                      <Link
                        href={category.url}
                        passHref
                        className={styles['navbar__category-link']}
                      >
                        {category.text}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Media>
            )}
          </div>
          <div
            className={classNames(styles['navbar__menu-center'], {
              [styles['navbar__menu-center--minimal']]: !isMobile && !showMenuIcon && !showBagIcon,
            })}
          >
            <Link
              href={getNavigationBaseUrl('/')}
              passHref
              className={classNames(
                styles.navbar__link,
                styles['navbar__link--logo'],
                styles['navbar__link-wrapper'],
                styles['navbar__link-wrapper--logo'],
              )}
              onClick={() => {
                if (isMobile) closeSubMenu()
              }}
              role="button"
              tabIndex={0}
              aria-label="Go to Everlane home"
            >
              <EverlaneLogo height="15px" />
            </Link>
          </div>

          {showBagIcon && desktopMenus?.length > 0 && (
            <div className={styles['navbar__menu-right']}>
              {showMenuIcon && !shouldShowMinimalNav && (
                <Media greaterThanOrEqual="largeTablet">
                  <div
                    className={classNames(
                      styles['navbar__link-wrapper'],
                      styles['navbar__link-wrapper--account'],
                    )}
                  >
                    <div
                      className={styles.navbar__link}
                      onClick={() => setIsAccountDropdownOpened(!isAccountDropdownOpened)}
                      role="button"
                      tabIndex={0}
                      aria-label="Open My Account dropdown"
                    >
                      Account
                    </div>

                    {desktopMenus && isAccountDropdownOpened && (
                      <AccountDropdown
                        setIsAccountDropdownOpened={setIsAccountDropdownOpened}
                        openAuthModal={({ isNewUser: isSigningUp }) => {
                          setIsAuthModalOpen(true)
                          setIsNewUser(isSigningUp)
                        }}
                      />
                    )}
                  </div>
                </Media>
              )}
            </div>
          )}
        </div>
      </div>

      {!isDesktop && (
        <MobileMenu
          isNavigationMenuOpen={isNavigationMenuOpen}
          closeModal={() => setIsNavigationMenuOpen(false)}
          topPosition={NAV_BAR_HEIGHT}
        />
      )}

      <AuthModal
        isOpen={isAuthModalOpen && !!appConfig}
        closeModal={() => {
          setIsAuthModalOpen(false)
          Cookie.set(Cookies.DISMISSED_LOGIN_GATE, Date.now())
        }}
        visitorEmail={visitorEmail}
        modalState={modalState}
      />
      {isRegionDropdownOpen && (
        <RegionDropdown
          isOpen={isRegionDropdownOpen}
          closeRegionDropdown={() => setIsRegionDropdownOpen(false)}
        />
      )}
    </nav>
  )
}

export default Navbar
